import { deepCopy } from '../utils/deep';
import { getUuidFromIri, getUuidV4, getIdFromItem } from './../utils/uuid';
import Watcher from './../utils/watcher';
// import { ACTIVITY_LOCKED } from './../mixins/mercure'

export const SET_LIST = 'set_list';
export const SET_TOTAL_ITEMS = 'set_total_items';
export const UPDATE_ONE = 'update_one';
export const SET_CURRENT = 'set_current';
export const DELETE_ONE = 'delete_one';
export const SET_IS_LOADING = 'set_is_loading';
export const SET_FILTERS = 'set_filters';
export const APPLY_FILTERS = 'apply_filters';
export const APPLY_LOCK_ACTIVITY = 'apply_lock_activity';
export const RESET_FETCH = 'reset_fetch';

const watcher = new Watcher(10000);

export class StoreBatchPayload {
  constructor ({
    items,
    batchDispatchMethod = 'fetchOne',
    symbol = null,
    getLastRequestInitiator = () => null,
    cancellable = false,
  }) {
    this.items = items;
    this.batchDispatchMethod = batchDispatchMethod;
    this.symbol = symbol;
    this.getLastRequestInitiator = getLastRequestInitiator;
    this.cancellable = cancellable;
  }
}

class StoreService {
  constructor (baseEndpoint, config = {}) {
    this.baseEndpoint = baseEndpoint;
    this.identifier = config.identifier || 'uuid';
    this.storePath = config.storePath || null;
    this.defaultFilters = config.defaultFilters || {};
    this.mercureListener = null;
  }

  getState () {
    return {
      mercureUpdates: {},
      list: {},
      totalItems: 0,
      current: null,
      filters: {},
      isLoading: {
        list: false,
        current: false,
      },
    };
  }

  getActions () {
    const baseEndpoint = this.baseEndpoint;
    const identifier = this.identifier;
    const storePath = this.storePath;
    const defaultFilters = this.defaultFilters;

    const processMercureUpdate = (context) => {
      return (response) => {
        if (this.storePath === null) {
          throw new Error('Store must have a \'storePath\' defined to use mercure');
        }

        context.commit('mercure/add_mercure_message', {
          uuid: getUuidV4(),
          storePath: this.storePath, // @todo: automatic lookup store namespace
          response,
          created: new Date(),
          confirmed: true, // @todo: Making it dynamic through an user action
        });
      };
    };

    const loadMercure = (response, context) => {
      if (this.mercureListener !== null) {
        this.mercureListener.disconnect();
      }

      try {
        if (context.$isClient()) {
          this.mercureListener = context.$createMercureListener(
            response,
            processMercureUpdate(context),
          );
        }
      } catch (e) {
        console.error(new Error('Error while loading mercure listener'), e);
      }
    };

    return {
      fetchList (context, filtersArg = {}) {
        const filters = { ...filtersArg };
        const useMercure = filters.mercure || false;

        delete filters.pathSuffix;

        context.commit(SET_IS_LOADING, { loader: 'list', isLoading: true });

        return this.$getDataProvider(baseEndpoint)
          .getList((filtersArg.pathSuffix || ''), { ...defaultFilters, ...filters })
          .then((response) => {
            context.commit(SET_CURRENT, null); // Clear reference
            // @todo: remove temp hack when the "/bulk-order/order-batches" response is normalized
            context.commit(SET_LIST, response.items || response);

            watcher.clear();
            if (useMercure === true) {
              loadMercure(response, this, context);

              watcher.init(() => {
                const list = { ...context.state.list };
                for (const uuid of Object.keys(list)) {
                  const entity = list[uuid] ? { ...list[uuid] } : null;

                  if (
                    entity
                    && entity.activity !== null
                    && entity.activity.expires_at < Math.floor(Date.now() / 1000)
                  ) {
                    entity.activity = null;
                    context.commit(UPDATE_ONE, entity);
                  }
                }
              });
            }

            context.commit(SET_TOTAL_ITEMS, response.totalItems);

            return response;
          })
          .catch((err) => {
            console.error(err);

            return null;
          })
          .finally(() => {
            context.commit(SET_IS_LOADING, { loader: 'list', isLoading: false });
          });
      },

      /**
       * @param context
       * @param {StoreBatchPayload} payload
       * @returns {Promise<*[]>}
       */
      // eslint-disable-next-line
      async fetchBatch ({}, batchData) { // @todo: use batch endpoints when implemented
        const {
          items,
          batchDispatchMethod,
          symbol,
          getLastRequestInitiator,
          cancellable,
        } = batchData;

        const { filteredItems } = items.reduce((acc, item) => {
          const { ids, filteredItems } = acc;

          if (item === null) {
            return { ids, filteredItems };
          }

          const id = getIdFromItem(item, identifier);
          if (ids[id] === undefined) {
            filteredItems.push(item);
          }

          ids[id] = true;

          return { ids, filteredItems };
        }, { ids: {}, filteredItems: [] });

        const batches = []; // @todo: remove batch splitting when batch endpoints are implemented
        const batchSize = this.$getEnv('API_BATCH_LIMIT', 10);

        filteredItems.forEach((item, idx) => {
          if (idx % batchSize === 0) {
            batches.push([]);
          }

          const batch = batches[batches.length - 1];

          const transformedItem = typeof item === 'string'
            ? { [identifier]: getIdFromItem(item), cancellable }
            : { ...item, cancellable };

          const request = async () => await this.dispatch(
            `${storePath}/${batchDispatchMethod}`,
            transformedItem,
          );

          batch.push(request);
        });

        const promiseResults = [];
        for (let idx = 0; idx < batches.length; idx += 1) {
          const lastRequestInitiator = getLastRequestInitiator();
          if (symbol && symbol !== lastRequestInitiator) {
            return [];
          }

          const batch = batches[idx];
          const batchResults = await Promise.all(batch.map((request) => request()));
          promiseResults.push(...batchResults);
        }

        return promiseResults;
      },

      fetchOne (context, item) {
        const useMercure = (typeof item === 'object') ? !!item.mercure : false;
        const id = getIdFromItem(item);

        const cancellable = typeof item === 'string' ? false : item.cancellable;

        context.commit(SET_IS_LOADING, { loader: 'current', isLoading: true });

        return this.$getDataProvider(baseEndpoint)
          .getOne('', { [identifier]: id, params: { cancellable } })
          .then((response) => {
            if (!(response && response.data)) {
              return null;
            }

            const item = response.data;
            context.commit(UPDATE_ONE, item);
            context.commit(SET_CURRENT, item);

            if (useMercure === true) {
              loadMercure(response, this, context);
            }

            return response;
          })
          .catch((err) => {
            console.error(err);

            return null;
          })
          .finally(() => {
            context.commit(SET_IS_LOADING, { loader: 'current', isLoading: false });
          });
      },

      updateOne (context, item = {}) {
        if (item[identifier] === undefined) {
          throw new Error('item.%s was not set'.replace('%s', identifier));
        }

        const EXCLUDE_KEYS = ['usePatch'];
        const newItem = Object.fromEntries(Object.entries(item).filter(([key]) => (
          !EXCLUDE_KEYS.includes(key)
        )));

        const usePatch = item?.usePatch || false;
        const itemIdentifier = item[identifier];

        return this.$getDataProvider(baseEndpoint)
          .update('', {
            data: newItem,
            [identifier]: itemIdentifier,
          }, usePatch)
          .then(async () => {
            // @todo: remove extra call when the backend consistently returns the full, updated resource
            const response = await context.dispatch('fetchOne', newItem);

            return response;
          })
          .then((response) => {
            const item = response.data;
            context.commit(UPDATE_ONE, item);
            context.commit(SET_CURRENT, item);

            return response;
          })
          .finally(() => {
            context.commit(SET_IS_LOADING, { loader: 'current', isLoading: false });
          });
      },

      deleteOne (context, item) {
        context.commit(SET_IS_LOADING, { loader: 'current', isLoading: true });

        return this.$getDataProvider(baseEndpoint)
          .delete('', { [identifier]: item[identifier] })
          .then((response) => {
            context.commit(DELETE_ONE, item);

            return response;
          }).finally(() => {
            context.commit(SET_IS_LOADING, { loader: 'current', isLoading: false });
          });
      },

      createOne (context, item) {
        context.commit(SET_IS_LOADING, { loader: 'current', isLoading: true });

        return this.$getDataProvider(baseEndpoint)
          .create('', {
            data: item,
          })
          .then((response) => {
            const item = response.data;
            context.commit(UPDATE_ONE, item);
            context.commit(SET_CURRENT, item);

            return response;
          }).finally(() => {
            context.commit(SET_IS_LOADING, { loader: 'current', isLoading: false });
          });
      },

      lock (context, item) {
        context.commit(SET_IS_LOADING, { loader: 'current', isLoading: true });
        if (item[identifier] === undefined) {
          throw new Error('item.%s was not set'.replace('%s', identifier));
        }

        const data = { user: context.rootState.account.user };
        const activity = { event: 'lock', data, timestamp: Date.now() / 1000 | 0 };

        context.commit(APPLY_LOCK_ACTIVITY, { item, activity });

        // await context.dispatch('unlockOthers', item)
        return this.$getDataProvider(baseEndpoint)
          .create('', {
            [identifier]: item[identifier],
            action: 'lock',
            data,
          }).finally(() => {
            context.commit(SET_IS_LOADING, { loader: 'current', isLoading: false });
          });
      },

      unlock (context, item) {
        context.commit(SET_IS_LOADING, { loader: 'current', isLoading: true });
        if (item[identifier] === undefined) {
          throw new Error('item.%s was not set'.replace('%s', identifier));
        }

        return this.$getDataProvider(baseEndpoint)
          .create('', {
            [identifier]: item[identifier],
            action: 'unlock',
          }).finally(() => {
            context.commit(SET_IS_LOADING, { loader: 'current', isLoading: false });
          });
      },
      // unlockOthers (context, item) {
      //   const promises = []
      //   for (const [uuid, storeItem] of Object.entries(context.state.list)) {
      //     if (storeItem.activity !== null &&
      //       storeItem.activity.event === ACTIVITY_LOCKED &&
      //       storeItem.activity.data.user.uuid === context.rootState.account.user.uuid &&
      //       item.uuid !== uuid
      //     ) {
      //       context.commit(APPLY_LOCK_ACTIVITY, { item: storeItem, activity: null })
      //       promises.push(context.dispatch('unlock', storeItem))
      //     }
      //   }
      //   return Promise.all(promises)
      // }
    };
  }

  getMutations () {
    const identifier = this.identifier;

    return {
      [SET_LIST] (state, payload = []) {
        const items = {};
        payload.forEach((item) => {
          const id = item[identifier] || getUuidFromIri(item['@id']);
          items[id] = {
            ...item,
            fetched_at: new Date(),
            fetched_details: false,
          };
        });

        state.list = items;
      },
      [SET_TOTAL_ITEMS] (state, payload = 0) {
        state.totalItems = payload;
      },
      [UPDATE_ONE] (state, item) {
        const list = { ...state.list };
        const id = item[identifier] || getUuidFromIri(item['@id']);

        list[id] = {
          ...item,
          fetched_at: new Date(),
          fetched_details: true,
        };

        state.list = list;
      },
      [SET_CURRENT] (state, item) {
        state.current = item !== null ? item[identifier] : null;
      },
      [DELETE_ONE] (state, item) {
        const list = { ...state.list };
        const id = item[identifier];
        delete list[id];
        state.list = list;
      },
      [SET_IS_LOADING] (state, { loader, isLoading }) {
        state.isLoading[loader] = isLoading;
      },
      [SET_FILTERS] (state, filters = {}) {
        const newFilters = deepCopy(filters);

        if (state.filters.page && state.filters.page === filters.page) {
          newFilters.page = 1;
        }

        state.filters = { ...newFilters };
      },
      [APPLY_FILTERS] (state, appliedFilters = {}) {
        state.filters = { ...state.filters, ...appliedFilters };
      },
      [APPLY_LOCK_ACTIVITY] (state, { item, activity }) {
        const list = { ...state.list };
        const id = item[identifier] || getUuidFromIri(item['@id']);

        state.list = { ...list, [id]: { ...item, activity } };
      },
      [RESET_FETCH] (state, item) {
        const list = { ...state.list };
        const id = item[identifier] || getUuidFromIri(item['@id']);

        list[id].fetched_at = new Date();
        list[id].fetched_details = false;
        state.list = list;
      },
    };
  }

  getGetters () {
    return {
      getList: (state) => {
        return state.list;
      },
      getOne: (state) => (identifier) => {
        return state.list[identifier];
      },
      getCurrent: (state, getters) => {
        return getters.getOne(state.current) || null;
      },
      isLoading: (state) => (loader) => {
        return state.isLoading[loader] || false;
      },
      getIriFromIdentifier: () => (identifier) => {
        return `/api/v1${this.baseEndpoint}/${identifier}`;
      },
    };
  }
}

export default StoreService;
